import { useCallback, useEffect, useRef } from 'react'
import { io } from 'socket.io-client'
import { EVENTS } from '@shared'
import type {
  AppointmentDeletedPayload,
  ClientToServerEvents,
  DailyNoteUpdatedPayload,
  ServerToClientEvents,
} from '@shared'
import type { Socket } from 'socket.io-client'
import type { Appointment } from '@shared/schemas'
import { useApiConfigValue, useNotification } from '@/core'
import { AVAILABILITY_KEYS, USER_KEYS, useUserDataValue  } from '@/features/users'
import { BOOKINGS_KEYS } from '@/features/bookings'
import { COMPANY_KEYS } from '@/features/companies'
import { PATIENT_KEYS } from '@/features/patients'
import { EXAM_KEYS } from '@/features/exams'
import { DOCUMENTS_KEYS } from '@/features/documents'
import { SITE_INSPECTION_KEYS } from '@/features/site-inspections'
import { JOB_ROLES_KEYS } from '@/features/job-roles'
import { CLINIC_KEYS } from '@/features/clinics'
import { TICKET_KEYS } from '@/features/tickets'

const socketUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000'

export const WebSocketProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { showNotification } = useNotification()
  const user = useUserDataValue()
  const { queryClient } = useApiConfigValue()

  // Use refs to access latest values in socket callbacks without re-triggering effect
  const userRef = useRef(user)
  const showNotificationRef = useRef(showNotification)

  useEffect(() => {
    userRef.current = user
  }, [user])

  useEffect(() => {
    showNotificationRef.current = showNotification
  }, [showNotification])

  const socketRef = useRef<Socket<
    ServerToClientEvents,
    ClientToServerEvents
  > | null>(null)

  const invalidateBookingsPage = useCallback(
    (date: string, doctorId?: string) => {
      queryClient.invalidateQueries({ queryKey: [`appointments?${date}`] })
      queryClient.invalidateQueries({
        queryKey: [AVAILABILITY_KEYS.slots.doctor(doctorId || '', date)],
      })
      queryClient.invalidateQueries({
        queryKey: [BOOKINGS_KEYS.summary.daily(date)],
      })
      queryClient.invalidateQueries({
        queryKey: [AVAILABILITY_KEYS.slots.multiDoctor(date)],
      })
      queryClient.invalidateQueries({
        queryKey: [`preacceptance-data/${date}`],
      })
    },
    [queryClient],
  )
  // Only admin users receive these updates
  const handleAvailabilityUpdate = useCallback(
    (role?: string) => {
      if (role === 'admin') {
        queryClient.invalidateQueries({ queryKey: ['doctor-slots'] })
        queryClient.invalidateQueries({ queryKey: ['doctor-month-status'] })
      }
    },
    [queryClient],
  )

  const handleCompanyUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [COMPANY_KEYS.list()] })
  }, [queryClient])

  const handlePatientUpdate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [PATIENT_KEYS.list()],
      exact: false,
    })
  }, [queryClient])

  const handleExamsUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [EXAM_KEYS.list()] })
  }, [queryClient])

  const handleJobRolesUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [JOB_ROLES_KEYS.list()] })
  }, [queryClient])

  const handleSiteInspectionsUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [SITE_INSPECTION_KEYS.list()] })
  }, [queryClient])

  const handleDocumentsUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_KEYS.list()] })
  }, [queryClient])

  const handleUsersUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [USER_KEYS.all] })
  }, [queryClient])

  const handleClinicsUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [CLINIC_KEYS.list()] })
  }, [queryClient])

  const handleClinicAssignmentsUpdate = useCallback(() => {
    // Invalidate all clinic assignments queries (they include date ranges in the key)
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0]?.toString().includes('clinics/assignments') ?? false,
    })
  }, [queryClient])
  useEffect(() => {
    // Connect to the specific namespace
    const socket = io(`${socketUrl}/notifications`, {
      transports: ['websocket'],
      withCredentials: true,
      autoConnect: true,
      forceNew: true, // Force a new connection to avoid reusing existing closed sockets
    }) as Socket<ServerToClientEvents, ClientToServerEvents>

    socketRef.current = socket

    const onConnect = () => {
      // Join global room to receive broadcast notifications
      socket.emit('join', 'global')
      // Join user specific room to receive targeted notifications (e.g. ticket updates)
      if (userRef.current?.uid) {
        socket.emit('join', `user:${userRef.current.uid}`)
      }
    }

    socket.on('connect', onConnect)

    socket.on(EVENTS.APPOINTMENT.CREATED, (data: Appointment) => {
      if (userRef.current?.role === 'admin') {
        showNotificationRef.current({
          type: 'info',
          message: 'Nuova prenotazione ricevuta',
          autoHideDuration: 5000,
        })
      }
      invalidateBookingsPage(data.date, data.doctorId ?? '')
    })

    socket.on(
      EVENTS.APPOINTMENT.UPDATED,
      (data: Appointment, oldDate?: string) => {
        if (userRef.current?.role === 'admin') {
          showNotificationRef.current({
            type: 'info',
            message: 'Prenotazione aggiornata',
            autoHideDuration: 5000,
          })
        }
        invalidateBookingsPage(data.date, data.doctorId ?? '')
        if (oldDate && oldDate !== data.date) {
          invalidateBookingsPage(oldDate, data.doctorId ?? '')
        }
      },
    )

    socket.on(EVENTS.APPOINTMENT.DELETED, (data: AppointmentDeletedPayload) => {
      if (userRef.current?.role === 'admin') {
        showNotificationRef.current({
          type: 'warning',
          message: 'Prenotazione cancellata',
          autoHideDuration: 5000,
        })
      }
      invalidateBookingsPage(data.date, data.doctorId)
    })

    // --- Schedule/Availability Events ---

    socket.on(EVENTS.AVAILABILITY.OVERRIDE_CREATED, () =>
      handleAvailabilityUpdate(userRef.current?.role),
    )
    socket.on(EVENTS.AVAILABILITY.OVERRIDE_DELETED, () =>
      handleAvailabilityUpdate(userRef.current?.role),
    )
    socket.on(EVENTS.AVAILABILITY.SLOT_STATE_CHANGED, () =>
      handleAvailabilityUpdate(userRef.current?.role),
    )
    socket.on(EVENTS.AVAILABILITY.SLOT_DURATION_RULE_CHANGED, (payload) => {
      queryClient.invalidateQueries({
        queryKey: [AVAILABILITY_KEYS.slotDurationRules(payload.doctorId)],
      })
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.some(
            (part) =>
              typeof part === 'string' &&
              part.includes(AVAILABILITY_KEYS.all.join('/')) &&
              part.includes('/slots/'),
          ),
      })
      payload.affectedDates.forEach((date) =>
        invalidateBookingsPage(date, payload.doctorId),
      )
      handleAvailabilityUpdate(userRef.current?.role)
    })

    socket.on(EVENTS.AVAILABILITY.DOCTOR_CHANGED, (payload) => {
      const currentUser = userRef.current
      if (currentUser?.role === 'admin') {
        // Show notification to admin users
        showNotificationRef.current({
          type: payload.isAvailable ? 'info' : 'warning',
          message: `${payload.displayName} è ora ${payload.isAvailable ? 'disponibile' : 'non disponibile'}`,
          autoHideDuration: 5000,
        })
        queryClient.invalidateQueries({ queryKey: [USER_KEYS.doctors.all()] })
      }
      // Invalidate queries relevant to doctor status
      if (currentUser?.role === 'doctor') {
        queryClient.invalidateQueries({
          queryKey: [USER_KEYS.detail(currentUser.uid)],
        })
      }
    })

    socket.on(EVENTS.AVAILABILITY.WEEKLY_CHANGED, () => {
      const currentUser = userRef.current
      if (currentUser?.role === 'admin') {
        // Invalidate queries relevant to weekly schedule
        queryClient.invalidateQueries({ queryKey: ['doctor-slots'] })
      }
    })

    // --- Daily Notes ---
    socket.on(EVENTS.DAILY_NOTE.UPDATED, (data: DailyNoteUpdatedPayload) => {
      if (data?.date) {
        queryClient.invalidateQueries({
          queryKey: [BOOKINGS_KEYS.summary.daily(data.date)],
        })
      }
    })

    // --- Companies ---
    socket.on(EVENTS.COMPANY.CREATED, handleCompanyUpdate)
    socket.on(EVENTS.COMPANY.UPDATED, handleCompanyUpdate)
    socket.on(EVENTS.COMPANY.DELETED, handleCompanyUpdate)

    // --- Patients ---
    socket.on(EVENTS.PATIENT.CREATED, handlePatientUpdate)
    socket.on(EVENTS.PATIENT.UPDATED, handlePatientUpdate)
    socket.on(EVENTS.PATIENT.DELETED, handlePatientUpdate)

    // --- Exams ---
    socket.on(EVENTS.EXAM.CREATED, handleExamsUpdate)
    socket.on(EVENTS.EXAM.UPDATED, handleExamsUpdate)
    socket.on(EVENTS.EXAM.DELETED, handleExamsUpdate)

    // --- Job Roles ---
    socket.on(EVENTS.JOB_ROLE.CREATED, handleJobRolesUpdate)
    socket.on(EVENTS.JOB_ROLE.UPDATED, handleJobRolesUpdate)
    socket.on(EVENTS.JOB_ROLE.DELETED, handleJobRolesUpdate)

    // --- Site Inspections ---
    socket.on(EVENTS.SITE_INSPECTION.CREATED, handleSiteInspectionsUpdate)
    socket.on(EVENTS.SITE_INSPECTION.UPDATED, handleSiteInspectionsUpdate)
    socket.on(EVENTS.SITE_INSPECTION.DELETED, handleSiteInspectionsUpdate)

    // --- Documents ---
    socket.on(EVENTS.DOCUMENT.CREATED, handleDocumentsUpdate)
    socket.on(EVENTS.DOCUMENT.UPDATED, handleDocumentsUpdate)
    socket.on(EVENTS.DOCUMENT.DELETED, handleDocumentsUpdate)

    // --- Users ---
    socket.on(EVENTS.USER.CREATED, handleUsersUpdate)
    socket.on(EVENTS.USER.UPDATED, handleUsersUpdate)
    socket.on(EVENTS.USER.DELETED, handleUsersUpdate)

    // --- Tickets ---
    socket.on(EVENTS.TICKET.UPDATED, (data: { id: string }) => {
      queryClient.invalidateQueries({ queryKey: [TICKET_KEYS.lists()] })
      queryClient.invalidateQueries({ queryKey: [TICKET_KEYS.detail(data.id)] })
    })

    if (user?.role === 'admin') {
      // --- Clinics ---
      socket.on(EVENTS.CLINIC.CREATED, handleClinicsUpdate)
      socket.on(EVENTS.CLINIC.UPDATED, handleClinicsUpdate)
      socket.on(EVENTS.CLINIC.DELETED, handleClinicsUpdate)

      // --- Clinic Assignments ---
      socket.on(EVENTS.CLINIC_ASSIGNMENT.CREATED, handleClinicAssignmentsUpdate)
      socket.on(EVENTS.CLINIC_ASSIGNMENT.UPDATED, handleClinicAssignmentsUpdate)
      socket.on(EVENTS.CLINIC_ASSIGNMENT.DELETED, handleClinicAssignmentsUpdate)
    }

    return () => {
      socket.removeAllListeners()
      socket.disconnect()
      socketRef.current = null
    }
  }, [
    handleAvailabilityUpdate,
    handleClinicAssignmentsUpdate,
    handleClinicsUpdate,
    handleCompanyUpdate,
    handleDocumentsUpdate,
    handleExamsUpdate,
    handleJobRolesUpdate,
    handlePatientUpdate,
    handleSiteInspectionsUpdate,
    handleUsersUpdate,
    invalidateBookingsPage,
    queryClient,
    user?.role,
  ]) // Empty dependencies array ensures single connection

  return <>{children}</>
}
